import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, Send } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAdminApi, errMsg } from "@/lib/api";
import { StatusSelect } from "@/admin/StatusSelect";

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b border-stone-100 text-sm">
    <dt className="text-stone-500">{k}</dt><dd className="col-span-2 font-medium break-words">{v || "-"}</dd>
  </div>
);

const Notes = ({ inquiry, onChanged }) => {
  const call = useAdminApi();
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try {
      await call({ method: "post", url: `/admin/inquiries/${inquiry.id}/notes`, data: { text } });
      setText("");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Notes</h3>
      <ul className="space-y-2" data-testid="inquiry-notes-list">
        {[...inquiry.notes].reverse().map((n, i) => (
          <li key={i} className="rounded-md bg-stone-50 border border-stone-200 p-3 text-sm">
            <p className="whitespace-pre-wrap">{n.text}</p>
            <p className="text-xs text-stone-400 mt-1">{new Date(n.at).toLocaleString()}</p>
          </li>
        ))}
      </ul>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Called, wants 2 containers in December"
        data-testid="inquiry-note-input" />
      <button onClick={add} data-testid="inquiry-note-add-btn"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white px-4 py-2 text-sm transition-colors">
        <Send className="h-3.5 w-3.5" /> Add note
      </button>
    </div>
  );
};

export const InquiryDetail = ({ inquiry, onClose, onChanged }) => {
  const call = useAdminApi();
  const wa = inquiry ? `https://wa.me/${inquiry.phone.replace(/\D/g, "")}` : "";
  const resend = async () => {
    try {
      const r = await call({ method: "post", url: `/admin/inquiries/${inquiry.id}/resend-alert` });
      toast[r.email_status === "sent" ? "success" : "error"](r.email_status === "sent" ? "Alert email sent" : "Email still failing");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Sheet open={!!inquiry} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="inquiry-detail">
        {inquiry && (
          <div className="space-y-6">
            <SheetHeader><SheetTitle className="text-2xl">{inquiry.company}</SheetTitle></SheetHeader>
            <div className="flex flex-wrap gap-2">
              <StatusSelect inquiry={inquiry} onDone={onChanged} />
              <a href={`tel:${inquiry.phone}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs" data-testid="inquiry-call-btn"><Phone className="h-3.5 w-3.5" /> Call</a>
              <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs" data-testid="inquiry-whatsapp-btn"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
              <a href={`mailto:${inquiry.email}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs" data-testid="inquiry-email-btn"><Mail className="h-3.5 w-3.5" /> Email</a>
            </div>
            <dl>
              <Row k="Product" v={`${inquiry.product_name} (${inquiry.incoterm})`} />
              <Row k="Quantity" v={inquiry.quantity} /><Row k="Destination" v={inquiry.destination_port} />
              <Row k="Country" v={inquiry.country} /><Row k="Contact" v={inquiry.contact_name} />
              <Row k="Email" v={inquiry.email} /><Row k="Phone" v={inquiry.phone} />
              <Row k="Message" v={inquiry.message} /><Row k="Received" v={new Date(inquiry.created_at).toLocaleString()} />
              <Row k="Email alert" v={inquiry.email_status} />
            </dl>
            {inquiry.email_status !== "sent" && (
              <button onClick={resend} className="text-xs underline text-amber-800" data-testid="inquiry-resend-alert-btn">Retry email alert</button>
            )}
            <Notes inquiry={inquiry} onChanged={onChanged} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
